import type {
  AppPreferences,
  Habit,
  LocalDateKey,
  Project,
  ProjectArea,
  SchoolSubject,
  StudyTopic,
  TaskCategory,
  TaskPriority,
  TaskTemplate,
  TaskType,
  TimeOfDay,
  UserSettings
} from '@/types';
import { createId } from './ids';

interface DefaultProjectDefinition {
  key: DefaultProjectKey;
  title: string;
  description: string;
  goal: string;
  area: ProjectArea;
  progressUnit: string;
}

export type DefaultProjectKey = 'school' | 'french' | 'sport' | 'novel' | 'youtube' | 'sleep';
export type DefaultProjectIds = Record<DefaultProjectKey, string>;

const DEFAULT_PROJECTS: readonly DefaultProjectDefinition[] = [
  {
    key: 'school',
    title: 'Zweites Gymjahr',
    description: 'Fächer, Lücken und Wiederholungen für das zweite Gymnasialjahr.',
    goal: 'Den Schulstoff zuverlässig verstehen und beherrschen.',
    area: 'school',
    progressUnit: '%'
  },
  {
    key: 'french',
    title: 'Französisch A1 zu A2',
    description: 'Strukturiertes tägliches Training in fünf Sprachbereichen.',
    goal: 'Das Sprachniveau A2 sicher erreichen.',
    area: 'french',
    progressUnit: 'Minuten'
  },
  {
    key: 'sport',
    title: '200 Push-ups',
    description: 'Sauberes, progressives Training mit Form- und Pausenprotokoll.',
    goal: 'Langfristig 200 saubere Liegestütze am Stück.',
    area: 'sport',
    progressUnit: 'Wiederholungen'
  },
  {
    key: 'novel',
    title: '200-Chapter Novel',
    description: 'Planung, Entwurf, Kontrolle und Upload der Novel-Kapitel.',
    goal: 'Eine konsistente Novel mit 200 fertigen Kapiteln.',
    area: 'novel',
    progressUnit: 'Kapitel'
  },
  {
    key: 'youtube',
    title: 'YouTube',
    description: 'Ideen und Videos durch eine klare Produktionspipeline führen.',
    goal: 'Regelmässig hochwertige Videos veröffentlichen.',
    area: 'youtube',
    progressUnit: 'Videos'
  },
  {
    key: 'sleep',
    title: 'Schlafrhythmus',
    description: 'Ein stabiler Rhythmus von 00:00 bis 08:00.',
    goal: 'Konstant um 00:00 schlafen und um 08:00 aufstehen.',
    area: 'sleep',
    progressUnit: 'Tage'
  }
] as const;

export function buildDefaultProjects(
  startDate: LocalDateKey,
  nowIso = new Date().toISOString()
): { projects: Project[]; ids: DefaultProjectIds } {
  const ids = Object.fromEntries(
    DEFAULT_PROJECTS.map((project) => [project.key, createId()])
  ) as DefaultProjectIds;
  return {
    ids,
    projects: DEFAULT_PROJECTS.map((definition) => ({
      id: ids[definition.key],
      title: definition.title,
      description: definition.description,
      goal: definition.goal,
      area: definition.area,
      startDate,
      status: 'active',
      progress: 0,
      progressUnit: definition.progressUnit,
      notes: '',
      createdAt: nowIso,
      updatedAt: nowIso
    }))
  };
}

export function buildDefaultUserSettings(nowIso = new Date().toISOString()): UserSettings {
  return {
    id: createId(),
    name: 'Yousif',
    primaryGoal: 'Jeden Tag konsequent an meinen wichtigsten Zielen arbeiten.',
    wakeTime: '08:00',
    bedTime: '00:00',
    activeAreas: ['school', 'french', 'sport', 'youtube', 'novel', 'sleep', 'personal'],
    dailyGoals: {
      schoolMinutes: 120,
      frenchMinutes: 90,
      youtubeMinutes: 60,
      novelChapters: 8,
      pushUps: 40,
      squats: 100
    },
    currentPushUpMaximum: 40,
    longTermPushUpGoal: 200,
    onboardingCompleted: false,
    createdAt: nowIso,
    updatedAt: nowIso
  };
}

export function buildDefaultAppPreferences(nowIso = new Date().toISOString()): AppPreferences {
  return {
    id: createId(),
    theme: 'dark',
    locale: 'de-CH',
    reducedMotion: false,
    compactMode: false,
    notificationPermission: 'not-asked',
    hasSeenInstallHelp: false,
    hasSeenNotificationExplanation: false,
    createdAt: nowIso,
    updatedAt: nowIso
  };
}

interface ScheduleDefinition {
  title: string;
  description?: string;
  startTime: TimeOfDay;
  endTime?: TimeOfDay;
  estimatedMinutes?: number;
  taskType: TaskType;
  category: TaskCategory;
  priority?: TaskPriority;
  targetValue?: number;
  unit?: string;
  project?: DefaultProjectKey;
  strict?: boolean;
}

const DEFAULT_SCHEDULE: readonly ScheduleDefinition[] = [
  {
    title: 'Aufstehen',
    startTime: '08:00',
    taskType: 'habit',
    category: 'sleep',
    project: 'sleep'
  },
  {
    title: 'Wasser trinken und Bett machen',
    startTime: '08:05',
    estimatedMinutes: 10,
    taskType: 'habit',
    category: 'routine',
    project: 'sleep'
  },
  {
    title: 'Frühstück und fertig machen',
    startTime: '08:15',
    endTime: '09:00',
    estimatedMinutes: 45,
    taskType: 'checkbox',
    category: 'routine'
  },
  {
    title: 'Schule',
    startTime: '09:00',
    endTime: '11:00',
    estimatedMinutes: 120,
    taskType: 'study',
    category: 'school',
    priority: 'mandatory',
    targetValue: 120,
    unit: 'minutes',
    project: 'school',
    strict: true
  },
  {
    title: 'Pause',
    startTime: '11:00',
    endTime: '11:30',
    estimatedMinutes: 30,
    taskType: 'break',
    category: 'break'
  },
  {
    title: 'Französisch',
    startTime: '11:30',
    endTime: '13:00',
    estimatedMinutes: 90,
    taskType: 'study',
    category: 'french',
    priority: 'mandatory',
    targetValue: 90,
    unit: 'minutes',
    project: 'french',
    strict: true
  },
  {
    title: 'Essen und Pause',
    startTime: '13:00',
    endTime: '14:00',
    estimatedMinutes: 60,
    taskType: 'break',
    category: 'break'
  },
  {
    title: 'YouTube',
    startTime: '14:00',
    endTime: '15:00',
    estimatedMinutes: 60,
    taskType: 'timed',
    category: 'youtube',
    priority: 'high',
    targetValue: 60,
    unit: 'minutes',
    project: 'youtube'
  },
  {
    title: 'Novel mit ChatGPT',
    startTime: '15:15',
    endTime: '17:15',
    estimatedMinutes: 120,
    taskType: 'chapter',
    category: 'novel',
    priority: 'mandatory',
    targetValue: 8,
    unit: 'chapters',
    project: 'novel',
    strict: true
  },
  {
    title: 'Sport',
    startTime: '17:30',
    endTime: '18:30',
    estimatedMinutes: 60,
    taskType: 'workout',
    category: 'sport',
    priority: 'high',
    targetValue: 60,
    unit: 'minutes',
    project: 'sport'
  },
  {
    title: 'Essen, duschen und Pause',
    startTime: '18:30',
    endTime: '20:00',
    estimatedMinutes: 90,
    taskType: 'break',
    category: 'break'
  },
  {
    title: 'Novel kontrollieren, hochladen oder Schule',
    startTime: '20:00',
    endTime: '21:00',
    estimatedMinutes: 60,
    taskType: 'project',
    category: 'novel',
    priority: 'high',
    project: 'novel'
  },
  {
    title: 'Freizeit',
    startTime: '21:00',
    endTime: '23:15',
    estimatedMinutes: 135,
    taskType: 'break',
    category: 'break'
  },
  {
    title: 'Tagesabschluss und morgigen Tag planen',
    startTime: '23:15',
    endTime: '23:30',
    estimatedMinutes: 15,
    taskType: 'checkbox',
    category: 'planning',
    priority: 'high'
  },
  {
    title: 'Schlafen',
    startTime: '00:00',
    taskType: 'sleep',
    category: 'sleep',
    priority: 'high',
    project: 'sleep'
  }
] as const;

export function buildDefaultTaskTemplates(
  startDate: LocalDateKey,
  projectIds: DefaultProjectIds,
  nowIso = new Date().toISOString()
): TaskTemplate[] {
  return DEFAULT_SCHEDULE.map((definition, sortOrder) => ({
    id: createId(),
    title: definition.title,
    description: definition.description ?? '',
    taskType: definition.taskType,
    category: definition.category,
    priority: definition.priority ?? 'normal',
    recurrence: { kind: 'daily', startDate, intervalDays: 1, paused: false },
    defaultStartTime: definition.startTime,
    ...(definition.endTime ? { defaultEndTime: definition.endTime } : {}),
    ...(definition.estimatedMinutes !== undefined
      ? { estimatedMinutes: definition.estimatedMinutes }
      : {}),
    ...(definition.targetValue !== undefined ? { defaultTargetValue: definition.targetValue } : {}),
    ...(definition.unit ? { unit: definition.unit } : {}),
    strictMode: definition.strict ? 'strict' : 'off',
    ...(definition.project ? { projectId: projectIds[definition.project] } : {}),
    active: true,
    effectiveFrom: startDate,
    revision: 1,
    sortOrder,
    notes: '',
    tags: ['standard-plan'],
    createdAt: nowIso,
    updatedAt: nowIso
  }));
}

export function buildDefaultHabits(
  templates: readonly TaskTemplate[],
  startDate: LocalDateKey,
  nowIso = new Date().toISOString()
): Habit[] {
  return templates
    .filter((template) => template.taskType === 'habit')
    .map((template) => ({
      id: createId(),
      taskTemplateId: template.id,
      title: template.title,
      category: template.category,
      recurrence: template.recurrence,
      targetValue: template.defaultTargetValue ?? 1,
      unit: template.unit ?? 'completion',
      active: true,
      startedOn: startDate,
      createdAt: nowIso,
      updatedAt: nowIso
    }));
}

const SUBJECT_NAMES = [
  'Mathematik',
  'Deutsch',
  'Französisch',
  'Englisch',
  'Biologie',
  'Chemie',
  'Physik',
  'Geschichte',
  'Geografie',
  'Informatik',
  'Wirtschaft und Recht'
] as const;

const START_TOPICS: Readonly<Record<string, readonly string[]>> = {
  Mathematik: [
    'Brüche',
    'Potenzen',
    'Wurzeln',
    'Gleichungen',
    'Terme',
    'Funktionen',
    'Graphen',
    'Prozentrechnung'
  ],
  Chemie: [
    'Atombau',
    'Periodensystem',
    'Ionen',
    'Moleküle',
    'chemische Bindungen',
    'Reaktionsgleichungen',
    'Stoffmenge',
    'Mol'
  ],
  Biologie: [
    'Zellaufbau',
    'Zellorganellen',
    'DNA',
    'Gene',
    'Mitose',
    'Meiose',
    'Enzyme',
    'Fotosynthese',
    'Zellatmung'
  ],
  Physik: [
    'Einheiten',
    'Geschwindigkeit',
    'Kraft',
    'Arbeit',
    'Energie',
    'Leistung',
    'Formeln umstellen'
  ]
};

export function buildDefaultSchoolData(
  schoolProjectId: string,
  nowIso = new Date().toISOString()
): { subjects: SchoolSubject[]; topics: StudyTopic[] } {
  const subjects = SUBJECT_NAMES.map((name, sortOrder): SchoolSubject => ({
    id: createId(),
    name,
    projectId: schoolProjectId,
    totalStudyMinutes: 0,
    notes: '',
    sortOrder,
    createdAt: nowIso,
    updatedAt: nowIso
  }));
  const topics = subjects.flatMap((subject) =>
    (START_TOPICS[subject.name] ?? []).map((title, sortOrder): StudyTopic => ({
      id: createId(),
      subjectId: subject.id,
      title,
      notes: '',
      totalStudyMinutes: 0,
      understanding: 1,
      openGaps: [],
      commonMistakes: [],
      practiceResults: [],
      examResults: [],
      sortOrder,
      createdAt: nowIso,
      updatedAt: nowIso
    }))
  );
  return { subjects, topics };
}

export interface InitialSeed {
  userSettings: UserSettings;
  appPreferences: AppPreferences;
  projects: Project[];
  taskTemplates: TaskTemplate[];
  habits: Habit[];
  schoolSubjects: SchoolSubject[];
  studyTopics: StudyTopic[];
  projectIds: DefaultProjectIds;
}

export function buildInitialSeed(
  startDate: LocalDateKey,
  nowIso = new Date().toISOString()
): InitialSeed {
  const { projects, ids } = buildDefaultProjects(startDate, nowIso);
  const taskTemplates = buildDefaultTaskTemplates(startDate, ids, nowIso);
  const school = buildDefaultSchoolData(ids.school, nowIso);
  return {
    userSettings: buildDefaultUserSettings(nowIso),
    appPreferences: buildDefaultAppPreferences(nowIso),
    projects,
    taskTemplates,
    habits: buildDefaultHabits(taskTemplates, startDate, nowIso),
    schoolSubjects: school.subjects,
    studyTopics: school.topics,
    projectIds: ids
  };
}
